import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@magnetic/button'
import { Flex } from '@magnetic/flex'
import { Heading } from '@magnetic/heading'
import { Text } from '@magnetic/text'
import { Modal } from '@magnetic/modal'
import { Input } from '@magnetic/input'
import { TextArea } from '@magnetic/text-area'
import { Notification, useToast } from '@magnetic/notification'
import {
  Plus,
  Warning,
  Trash,
  CheckCircle
} from '@/lib/icons'
import { listProjects } from '@/lib/api'
import { ProjectsTable } from '@/components/projects-table'
import { useModalStore } from '@/stores'
import { useProjectActions } from '@/hooks/useProjectActions'
import type { Project as ProtoProject } from '@/lib/generated-types'
import type { ValidateRepositoryResponse } from '@/gen/catpocs/v1/admin_pb'

type Project = Pick<ProtoProject, 'id' | 'name' | 'applicationCount'> & {
  displayName?: string
}

export function ProjectsPage() {
  const navigate = useNavigate()
  const { openModal, closeModal, isOpen } = useModalStore()
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    repoUrl: '',
    tags: '',
  })
  const [validationResults, setValidationResults] = useState<Array<{ check: string; passed: boolean; message: string }>>([])
  const [selectedProjects, setSelectedProjects] = useState<Project[]>([])

  const { addToast } = useToast()
  const {
    createProjectMutation,
    validateRepositoryMutation,
    deleteProjects,
    isDeletingProjects,
  } = useProjectActions({
    onCreateCompleted: () => {
      closeModal()
      setFormData({ name: '', description: '', repoUrl: '', tags: '' })
    },
    onProjectCreated: (projectId) => {
      if (projectId) {
        navigate(`/projects/${projectId}`)
      }
    },
  })

  const handleNewProject = () => {
    openModal('create-project')
  }

  const handleDelete = async () => {
    if (selectedProjects.length === 0) return

    await deleteProjects(selectedProjects)
    closeModal()
    setSelectedProjects([])
  }

  const openConfirmDelete = (projects: Project[]) => {
    setSelectedProjects(projects)
    openModal('confirm-delete')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.name.trim()) {
      addToast({
        status: 'warning',
        title: 'Validation error',
        children: 'Project name is required',
      })
      return
    }

    if (!formData.repoUrl.trim()) {
      addToast({
        status: 'warning',
        title: 'Validation error',
        children: 'Repository URL is required',
      })
      return
    }

    setValidationResults([])
    const validationResponse = await validateRepositoryMutation.mutateAsync({
      repoUrl: formData.repoUrl.trim(),
      helmPath: 'helm',
    }) as ValidateRepositoryResponse

    if (!validationResponse.valid) {
      setValidationResults(validationResponse.results || [])
      addToast({
        status: 'warning',
        title: 'Repository validation failed',
        children: 'Fix the repository issues before creating the project.',
      })
      return
    }

    createProjectMutation.mutate({
      displayName: formData.name,
      description: formData.description,
      repoUrl: formData.repoUrl,
      tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean),
    })
  }

  return (
    <>
      <Flex direction="vertical" gap={24}>
        {/* Page header */}
        <Flex direction="vertical" gap={4}>
          <Heading size="primary">Projects</Heading>
          <Text color="light">Manage your ArgoCD projects and applications</Text>
        </Flex>

        {/* Projects table */}
        <ProjectsTable
          queryFn={listProjects}
          selectableRows={true}
          configComponents={
            <Button kind="primary" icon={<Plus weight="bold" />} onClick={handleNewProject}>
              New project
            </Button>
          }
          bulkActions={(selectedCount, selectedRows) => (
            <>
              <Text size="p3">{selectedCount} selected</Text>
              <Button
                kind="secondary"
                size="sm"
                destructive
                icon={<Trash weight="bold" size={16} />}
                onClick={() => openConfirmDelete(selectedRows)}
              >
                Delete
              </Button>
            </>
          )}
          initialPageSize={10}
        />
      </Flex>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isOpen('confirm-delete')}
        onClose={() => !isDeletingProjects && closeModal()}
        size="sm"
      >
        <Modal.Content>
          <Flex direction="vertical" gap={16}>
            <Flex direction="horizontal" gap={12} align="center">
              <Warning size={24} weight="bold" color="var(--color-warning)" />
              <Text size="h3" weight="bold">
                Delete Projects
              </Text>
            </Flex>

            <Text color="secondary">
              Are you sure you want to delete {selectedProjects.length} project{selectedProjects.length > 1 ? 's' : ''}? This will permanently remove all associated applications and cannot be undone.
            </Text>

            {selectedProjects.length > 0 && (
              <Flex direction="vertical" gap={4} style={{
                padding: 12,
                backgroundColor: 'var(--color-background-secondary)',
                borderRadius: 8,
                maxHeight: 200,
                overflowY: 'auto'
              }}>
                {selectedProjects.map(project => (
                  <Text key={project.id} size="sm" family="mono">
                    • {project.displayName || project.name}
                    {project.applicationCount ? ` (${project.applicationCount} apps)` : ''}
                  </Text>
                ))}
              </Flex>
            )}

            <Flex direction="horizontal" justify="flex-end" gap={12}>
              <Button
                kind="tertiary"
                onClick={() => closeModal()}
                disabled={isDeletingProjects}
              >
                Cancel
              </Button>
              <Button
                destructive
                onClick={handleDelete}
                loading={isDeletingProjects}
                icon={<Trash weight="bold" color="white" />}
              >
                {isDeletingProjects ? 'Deleting...' : 'Delete'}
              </Button>
            </Flex>
          </Flex>
        </Modal.Content>
      </Modal>

      {/* Create Project Modal */}
      <Modal isOpen={isOpen('create-project')} onClose={closeModal} size="md">
        <Modal.Content>
          <form onSubmit={handleSubmit}>
            <Heading size="section" style={{ marginBottom: 8 }}>Create new project</Heading>
            <Text color="light" style={{ marginBottom: 24 }}>
              Add a new project to manage your ArgoCD applications
            </Text>

            <Flex direction="vertical" gap={16}>
              <Input
                label="Display name"
                placeholder="My Awesome Project"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                hint="A user-friendly name. A URL-safe slug will be auto-generated."
              />

              <TextArea
                label="Description"
                placeholder="A brief description of your project"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />

              <Input
                label="Repository URL"
                placeholder="https://github.com/org/repo"
                value={formData.repoUrl}
                onChange={(e) => setFormData({ ...formData, repoUrl: e.target.value })}
                required
              />

              {validationResults.length > 0 && (
                <Notification
                  status={validationResults.every((result) => result.passed) ? 'positive' : 'warning'}
                  title="Repository validation"
                >
                  <Flex direction="vertical" gap="xs">
                    {validationResults.map((result) => (
                      <Flex key={result.check} align="center" gap="sm">
                        {result.passed ? <CheckCircle size={16} /> : <Warning size={16} />}
                        <Text size="p4">{result.message}</Text>
                      </Flex>
                    ))}
                  </Flex>
                </Notification>
              )}

              <Input
                label="Tags"
                placeholder="production, backend, customer-facing"
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                hint="Comma-separated list of tags"
              />
            </Flex>

            <Flex direction="horizontal" justify="flex-end" gap={12} style={{ marginTop: 24 }}>
              <Button
                kind="tertiary"
                onClick={closeModal}
                disabled={createProjectMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                kind="primary"
                type="submit"
                loading={createProjectMutation.isPending || validateRepositoryMutation.isPending}
                icon={<Plus weight="bold" />}
              >
                {validateRepositoryMutation.isPending
                  ? 'Validating...'
                  : createProjectMutation.isPending
                    ? 'Creating...'
                    : 'Create project'}
              </Button>
            </Flex>
          </form>
        </Modal.Content>
      </Modal>
    </>
  )
}
